import {Injectable} from "@angular/core";
import {BehaviorSubject, Observable} from "rxjs";
import {NotificationService, Notification} from "@pb-labs/notification";
import {invalidFileMessage} from "./const";
import {MessageResponse} from "@app/services/sftp-api/models";
import {SFTPAPIService} from "@app/services/sftp-api/sftp-api.service";
import {FileUploadModalBaseModel} from "@app/modules/browse/components/shared/file-upload-modal/services/file-upload-modal-base.model";
import {FileOperationsService} from "@app/services/file-operations.service";
import {takeUntil} from "rxjs/operators";

@Injectable()
export class PublicKeyUploadModalService extends FileUploadModalBaseModel {
  private _newPublicKey$: BehaviorSubject<string> = null;
  private _validationErrorMessage: string = null;

  constructor(
    protected sftpAPIService: SFTPAPIService,
    protected notificationService: NotificationService,
    private fileOperationsService: FileOperationsService
  ) {
    super();
    this.sftpAPIService = sftpAPIService;
    this.notificationService = notificationService;
  }

  get validationErrorMessage(): string {
    return this._validationErrorMessage;
  }

  get newPublicKey$(): Observable<string> {
    return this._newPublicKey$.asObservable();
  }

  initialize() {
    super.initialize();
    this.fileOperationsService.initialize();

    this._newPublicKey$ = new BehaviorSubject<string>(null);
  }

  destroy(): void {
    super.destroy();
    this.fileOperationsService.destroy();

    this._validationErrorMessage = null;

    this._newPublicKey$.complete();
    this._newPublicKey$ = null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  startDialog$(): Observable<any> {
    return new Observable<void>((subscriber) => {
      this._resultSubscriber$ = subscriber;

      this._openDialog();
    });
  }

  onFileSelect(selectedFile: File): void {
    super.onFileSelect(selectedFile);

    this.getFileContent(selectedFile)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (content) => {
          this._newPublicKey$.next(content);
        },
        error: (error) => {
          this.notificationService.showError(
            new Notification({
              message: error.message,
              timeout: 5000,
            })
          );
        },
      });

    this._validationErrorMessage = this.validateUploadedFile(selectedFile);
  }

  uploadFile(): Observable<MessageResponse> {
    const formData = new FormData();
    formData.append("file_to_upload", this._selectedFile$.value);
    formData.append("file_name", this._selectedFile$.value.name);

    return this.sftpAPIService.uploadPubKeyFile(formData);
  }

  clearFile(): void {
    super.clearFile();
    this._validationErrorMessage = null;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected handleSuccessUpload(result: MessageResponse): void {
    super.handleSuccessUpload(this._newPublicKey$.value);
  }

  protected closeUploadModal(): void {
    this._validationErrorMessage = null;
    super.closeUploadModal();
  }

  private getFileContent(file: File): Observable<string> {
    return this.fileOperationsService.readFile(file);
  }

  private validateUploadedFile(file: File): string {
    let validationMessage = null;

    if (!file) {
      validationMessage = invalidFileMessage;
    }

    return validationMessage;
  }
}
